import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaExclamationTriangle } from 'react-icons/fa';
import {
    createUserAPI,
    updateUserAPI,
    sendPasswordResetAPI,
    searchAdminsForLinkingAPI,
    linkAdminToServerAPI,
} from '../services/serverService';

const Spinner = () => <div className="spinner"></div>;

const getFunctionMessage = (err, fallback = 'Operation failed.') => {
    return err?.response?.data?.message || err?.message || fallback;
};

const AddAdminModal = ({ serverId, closeModal, refreshList, adminToEdit, currentLimits }) => {
    const navigate = useNavigate();
    const isEditMode = Boolean(adminToEdit);

    const [view, setView] = useState(isEditMode ? 'create' : 'search');
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [searchHasRun, setSearchHasRun] = useState(false);
    const [selectedExistingAdmin, setSelectedExistingAdmin] = useState(null);
    const [existingUsernameWarning, setExistingUsernameWarning] = useState('');

    const [formData, setFormData] = useState({
        username: '',
        fullName: '',
        email: '',
        phoneCode: '+44',
        phoneNumber: '',
        password: '',
    });

    const [isSaving, setIsSaving] = useState(false);
    const [apiError, setApiError] = useState('');
    const [resetEmailSent, setResetEmailSent] = useState(false);

    useEffect(() => {
        if (isEditMode && adminToEdit) {
            setView('create');
            setFormData({
                username: adminToEdit.username || '',
                fullName: adminToEdit.fullName || '',
                email: adminToEdit.email || '',
                phoneCode: adminToEdit.phoneCode || '+44',
                phoneNumber: adminToEdit.phoneNumber || '',
                password: '',
            });
        }
    }, [isEditMode, adminToEdit]);

    const { fullName } = formData;
    useEffect(() => {
        if (!isEditMode && view === 'create' && typeof selectedExistingAdmin !== 'undefined' ? !selectedExistingAdmin : true) {
            if (fullName.trim() === '') {
                setFormData(prev => ({ ...prev, username: '' }));
                return;
            }
            const nameParts = fullName.trim().split(/\s+/);
            const suggestion = nameParts.map((part, index) => {
                if (index === 0) return part.toLowerCase();
                return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
            }).join('');
            setFormData(prev => ({ ...prev, username: suggestion }));
        }
    }, [fullName, isEditMode, view, selectedExistingAdmin]);

    const handleSendResetEmail = async () => {
        const confirmAlert = window.confirm(`Send a password reset email to ${formData.email}?`);
        if (!confirmAlert) return;

        try {
            const targetUser = adminToEdit;
            // SSO ARCHITECTURE: Priority ID lookup for global identity
            const targetId = targetUser.username || targetUser.id || targetUser.uid || targetUser.email;
            
            await sendPasswordResetAPI({
                userId: targetId,
                email: formData.email,
                username: formData.username,
                fullName: formData.fullName,
            });
            setResetEmailSent(true);
            alert(`Password reset email sent safely to ${formData.email}!`);
        } catch (error) {
            console.error('Reset Password Error:', error);
            alert('Failed to send reset email. Please check the logs.');
        }
    };

    const runSearch = async (overrideQuery = null) => {
        const query = String(overrideQuery ?? searchTerm).trim();
        setApiError('');
        setExistingUsernameWarning('');
        setSearchHasRun(true);
        setSelectedExistingAdmin(null);

        if (query.length < 2) {
            setSearchResults([]);
            setApiError('Type at least 2 characters to search. You can still create a new admin without searching.');
            return;
        }

        setIsSearching(true);
        try {
            const response = await searchAdminsForLinkingAPI(query, serverId);
            setSearchResults(response.data?.data || []);
        } catch (err) {
            console.error('Admin search failed:', err);
            setSearchResults([]);
            setApiError('Search failed, but you can still create a new admin below.');
        } finally {
            setIsSearching(false);
        }
    };

    const handleSelectExistingAdmin = (admin) => {
        setSelectedExistingAdmin(admin);
        setApiError('');
        setExistingUsernameWarning('');
    };

    const handleLinkExistingAdmin = async (admin, overrideExisting = false) => {
        setIsSaving(true);
        setApiError('');
        try {
            const response = await linkAdminToServerAPI({
                targetServerId: serverId,
                sourceServerId: admin.sourceServerId,
                sourceAdminId: admin.sourceAdminId || admin.id,
                username: admin.username,
                overrideExisting,
            });

            if (response.data?.status === 'exists-on-target' && !overrideExisting) {
                const proceed = window.confirm(`${response.data.message}\n\nReplace/repair the admin record on this server and continue linking?`);
                if (proceed) {
                    await handleLinkExistingAdmin(admin, true);
                }
                return;
            }

            alert(`Admin '${admin.username}' linked to this server. ${response.data?.data?.emailSent ? 'Account email sent.' : 'Email was not sent; check function logs if needed.'}`);
            refreshList();
            closeModal();
        } catch (err) {
            console.error('Link admin failed:', err);
            setApiError(getFunctionMessage(err, 'Failed to link existing admin.'));
        } finally {
            setIsSaving(false);
        }
    };

    const handleCreateAnyway = () => {
        setSelectedExistingAdmin(null);
        setApiError('');
        setExistingUsernameWarning('');
        setView('create');
    };

    const handleChange = (e) => {
        let { name, value } = e.target;
        if (name === 'username') value = value.replace(/\s+/g, '');
        setExistingUsernameWarning('');
        setFormData(prevState => ({ ...prevState, [name]: value }));
    };

    const handleSubmit = async (e, shouldRedirect = false, bypassLinkUid = null) => {
        if (e) e.preventDefault();
        setIsSaving(true);
        setApiError('');
        setExistingUsernameWarning('');

        try {
            let newUserId = null;
            if (isEditMode) {
                // SSO ARCHITECTURE: Ensure updates target the global identity priority
                const targetId = adminToEdit.username || adminToEdit.id || adminToEdit.uid || adminToEdit.email;
                await updateUserAPI(targetId, formData);
                alert('Admin updated successfully!');
            } else {
                const payload = { ...formData, role: 'admin', serverIds: [serverId] };
                
                // SSO ARCHITECTURE: Attach bypass flag if user confirmed linking
                if (bypassLinkUid) payload.forceLinkUid = bypassLinkUid;
                
                const response = await createUserAPI(payload);
                
                // SSO ARCHITECTURE: Catch the linking requirement from the backend
                const resData = response.data || response;
                if (resData && resData.status === 'requires_linking') {
                    setIsSaving(false);
                    const proceed = window.confirm(resData.message);
                    if (proceed) {
                        return handleSubmit(null, shouldRedirect, resData.uid);
                    } else {
                        return; // User cancelled
                    }
                }

                newUserId = formData.username;
                alert('New admin created successfully!');
            }

            refreshList();
            if (shouldRedirect && newUserId) {
                navigate(`/servers/${serverId}/admins/${newUserId}`, { state: { openEditModal: true } });
            } else {
                closeModal();
            }
        } catch (err) {
            const message = getFunctionMessage(err, 'Operation failed.');
            if (!isEditMode && (message.includes('USERNAME_EXISTS') || message.toLowerCase().includes('username'))) {
                const username = formData.username;
                setExistingUsernameWarning(`Username '${username}' already exists. That username is treated as the same SSO identity. Search and link that admin instead, or change the username to create a separate admin. Same real email or phone is allowed when the username is different.`);
                setSearchTerm(username);
                setView('search');
                setTimeout(() => runSearch(username), 0);
            } else {
                setApiError(message);
            }
        } finally {
            setIsSaving(false);
        }
    };

    const isOverLimit = currentLimits && currentLimits.maxLimit > 0 && currentLimits.currentCount >= currentLimits.maxLimit;

    const renderLimitWarning = () => {
        if (!isOverLimit || isEditMode) return null;
        return (
            <div style={{ backgroundColor: '#fffff0', border: '1px solid #d69e2e', color: '#975a16', padding: '12px', borderRadius: '6px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
                <FaExclamationTriangle size={18} />
                <div><strong>License Limit Reached</strong> ({currentLimits.currentCount} / {currentLimits.maxLimit} Staff Members). Adding this user will result in overage charges.</div>
            </div>
        );
    };

    const renderLinkedServers = (admin) => {
        const linkedServers = admin.linkedServers || [];
        if (linkedServers.length === 0) return null;
        return (
            <div style={{ fontSize: '12px', marginTop: '6px', color: '#4a5568' }}>
                <strong>Linked servers:</strong> {linkedServers.map(s => s.serverName || s.serverId).join(', ')}
            </div>
        );
    };

    const renderSearchView = () => (
        <>
            <h2>Add Admin to Server</h2>
            {renderLimitWarning()}
            <p className="modal-subtitle">
                Search existing admins by username, email, full name, or phone. Username is the SSO identity key; same email/phone alone does not force linking.
            </p>

            {existingUsernameWarning && (
                <div style={{ backgroundColor: '#fff5f5', border: '1px solid #fc8181', color: '#742a2a', padding: '12px', borderRadius: '6px', marginBottom: '12px', fontSize: '13px' }}>
                    {existingUsernameWarning}
                </div>
            )}

            <div className="form-group full-width">
                <label>Search by Username, Email, Full Name, or Phone</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') runSearch(); }}
                        placeholder="e.g., username, email, full name, or phone number"
                    />
                    <button type="button" className="button-primary" onClick={() => runSearch(searchTerm)} disabled={isSearching}>
                        {isSearching ? <Spinner /> : 'Search'}
                    </button>
                </div>
            </div>

            {apiError && <p className="error-message">{apiError}</p>}

            <div className="search-results-container">
                {searchResults.length > 0 ? (
                    searchResults.map(admin => (
                        <div className="search-result-item" key={`${admin.username}-${admin.sourceServerId}-${admin.sourceAdminId}`}>
                            <div>
                                <strong className="search-result-name">{admin.fullName || admin.username}</strong>
                                <span className="search-result-role admin">admin</span>
                                <div className="search-result-details">
                                    {admin.email || 'No email'} | {admin.username} | {admin.phoneNumber || 'No phone'}
                                </div>
                                <div style={{ fontSize: '12px', marginTop: '4px', color: '#718096' }}>
                                    Matched by: {(admin.matchedBy || []).join(', ') || 'unknown'}
                                </div>
                                {renderLinkedServers(admin)}
                                {admin.alreadyLinkedToTarget && (
                                    <div style={{ fontSize: '12px', marginTop: '4px', color: '#975a16' }}>
                                        Already appears to be linked to this target server. You can repair/replace if needed.
                                    </div>
                                )}
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <button
                                    className="button-primary add-existing-button"
                                    onClick={() => handleLinkExistingAdmin(admin, false)}
                                    disabled={isSaving}
                                >
                                    {isSaving ? <Spinner /> : (admin.alreadyLinkedToTarget ? 'Repair / Replace' : 'Link Existing')}
                                </button>
                                <button
                                    type="button"
                                    className="button-secondary"
                                    onClick={() => handleSelectExistingAdmin(admin)}
                                    disabled={isSaving}
                                >
                                    Review
                                </button>
                            </div>
                        </div>
                    ))
                ) : (!isSearching && searchHasRun && <p className="no-data-cell">No existing admins found. You can create a new admin.</p>)}
            </div>

            {selectedExistingAdmin && (
                <div style={{ marginTop: '14px', padding: '12px', border: '1px solid #cbd5e0', borderRadius: '6px', background: '#f7fafc' }}>
                    <strong>Selected existing admin:</strong> {selectedExistingAdmin.fullName || selectedExistingAdmin.username}<br />
                    <span style={{ fontSize: '13px' }}>Username: {selectedExistingAdmin.username}</span><br />
                    <span style={{ fontSize: '13px' }}>Email: {selectedExistingAdmin.email || 'N/A'}</span><br />
                    <span style={{ fontSize: '13px' }}>Phone: {selectedExistingAdmin.phoneNumber || 'N/A'}</span>
                    {renderLinkedServers(selectedExistingAdmin)}
                    <div style={{ marginTop: '10px', display: 'flex', gap: '8px' }}>
                        <button className="button-primary" onClick={() => handleLinkExistingAdmin(selectedExistingAdmin, false)} disabled={isSaving}>
                            {isSaving ? <Spinner /> : 'Confirm Link Existing'}
                        </button>
                        <button className="button-secondary" onClick={() => setSelectedExistingAdmin(null)} disabled={isSaving}>Clear</button>
                    </div>
                </div>
            )}

            <div className="modal-actions" style={{ justifyContent: 'space-between', marginTop: '20px' }}>
                <button type="button" className="button-secondary" onClick={closeModal} disabled={isSaving}>Cancel</button>
                <button type="button" className="button-primary" onClick={handleCreateAnyway} disabled={isSaving}>+ Create New Admin / Skip Search</button>
            </div>
        </>
    );

    const renderCreateEditView = () => (
        <>
            <h2>{isEditMode ? 'Edit Admin' : 'Create New Admin'}</h2>
            {renderLimitWarning()}
            {!isEditMode && (
                <div style={{ backgroundColor: '#ebf8ff', border: '1px solid #90cdf4', color: '#2a4365', padding: '12px', borderRadius: '6px', marginBottom: '15px', fontSize: '13px' }}>
                    Same real email or phone can be used for a separate admin if the username is different. If the username already exists, the app will ask you to link that SSO identity instead.
                </div>
            )}
            <form>
                <div className="form-grid">
                    <div className="form-group"><label>Full Name</label><input name="fullName" value={formData.fullName} onChange={handleChange} required /></div>
                    <div className="form-group"><label>Username</label><input name="username" value={formData.username} onChange={handleChange} required disabled={isEditMode} /></div>
                    <div className="form-group"><label>Email Address</label><input type="email" name="email" value={formData.email} onChange={handleChange} required /></div>

                    <div className="form-group">
                        <label>Phone Number</label>
                        <div style={{ display: 'flex', gap: '5px' }}>
                            <input
                                type="text"
                                name="phoneCode"
                                value={formData.phoneCode}
                                onChange={handleChange}
                                placeholder="+44"
                                style={{ width: '60px', padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }}
                            />
                            <input type="tel" name="phoneNumber" value={formData.phoneNumber} onChange={handleChange} placeholder="7575944496" style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ccc' }} />
                        </div>
                    </div>
                </div>

                <div className="form-group full-width" style={{ marginTop: '1.5rem' }}>
                    <label>Password</label>
                    <input type="password" name="password" placeholder={isEditMode ? 'Leave blank to keep unchanged' : ''} value={formData.password} onChange={handleChange} required={!isEditMode} />
                </div>

                {isEditMode && (
                    <div style={{ marginTop: '1rem', padding: '10px', backgroundColor: '#f9f9f9', border: '1px solid #ddd', borderRadius: '6px' }}>
                        <p style={{ margin: '0 0 10px 0', fontSize: '0.9rem' }}><strong>Security Options:</strong></p>
                        <button type="button" className="button-secondary" onClick={handleSendResetEmail} disabled={resetEmailSent}>
                            {resetEmailSent ? 'Reset Email Sent ✓' : 'Send Password Reset Email'}
                        </button>
                    </div>
                )}

                {existingUsernameWarning && <p className="error-message">{existingUsernameWarning}</p>}
                {apiError && <p className="error-message">{apiError}</p>}

                <div className="modal-actions" style={{ gap: '10px' }}>
                    <button type="button" className="button-secondary" onClick={isEditMode ? closeModal : () => setView('search')}>{isEditMode ? 'Cancel' : 'Back to Search'}</button>
                    <button type="button" className={isEditMode ? 'button-primary' : 'button-primary-outline'} disabled={isSaving} onClick={(e) => handleSubmit(e, false)}>
                        {isSaving ? <Spinner /> : (isEditMode ? 'Save Changes' : 'Quick Create')}
                    </button>
                    {!isEditMode && (
                        <button type="button" className="button-primary" disabled={isSaving} style={{ display: 'flex', alignItems: 'center', gap: '5px' }} onClick={(e) => handleSubmit(e, true)}>
                            {isSaving ? <Spinner /> : <>Create & Edit Profile <FaArrowRight /></>}
                        </button>
                    )}
                </div>
            </form>
        </>
    );

    return (
        <div className="modal-overlay">
            <div className="modal-content large-modal">
                {view === 'search' && !isEditMode ? renderSearchView() : renderCreateEditView()}
            </div>
        </div>
    );
};

export default AddAdminModal;